import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDef } from '@tanstack/react-table';

import { ScenarioOrderBy } from '@/api/generated.schemas';
import { ColumnHeader } from '@/components/table/column-header';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { formatDateForDisplay } from '@/lib/utils';
import type { Scenario } from '@/routes/(app)/(scenarios)/types';
import { isScenarioProgressComplete } from '@/routes/(app)/(scenarios)/utils/scenarios-list.utils';

export const useScenariosTableColumns = (): Array<ColumnDef<Scenario>> => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        accessorKey: ScenarioOrderBy.creation_date,
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            enableSorting={true}
            title={t('scenarios.table.creation_date')}
          />
        ),
        cell: ({ row }) => (
          <p>{formatDateForDisplay(row.original.creation_date ?? '')}</p>
        ),
      },
      {
        id: ScenarioOrderBy.total_simulation_runs,
        accessorFn: (row) => row.total_simulation_runs,
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            enableSorting={true}
            title={t('scenarios.table.simulation_runs')}
          />
        ),
        cell: ({ row }) => <p>{row.original.total_simulation_runs}</p>,
      },
      {
        accessorKey: ScenarioOrderBy.progress,
        header: ({ column }) => (
          <ColumnHeader
            column={column}
            enableSorting={true}
            title={t('scenarios.table.progress')}
          />
        ),
        cell: ({ row }) =>
          isScenarioProgressComplete(row.original.progress) ? (
            <Badge variant={'success'}>{t('scenarios.table.completed')}</Badge>
          ) : (
            <Badge
              variant={'outline'}
              className='flex w-fit items-center gap-2'>
              <Spinner className='w-4 h-4' /> {t('scenarios.table.processing')}
            </Badge>
          ),
      },
    ],
    [t]
  );
};
